import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Globe, Shield, LayoutGrid, ListChecks, Store } from 'lucide-react';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';
import { useDeviceAppMode } from '@/hooks/useDeviceAppMode';
import { Skeleton } from '@/components/ui/skeleton';
import { ProfileAvatar } from '@/components/ProfileAvatar';

const appManagementOptions = [
  {
    id: 'apps-by-category',
    title: 'Apps by Category',
    description: 'Apps are organized into categories like Social, Games, Productivity, and more. You control which categories are allowed.',
    store: 'Shomri App Store',
    icon: LayoutGrid,
    gradient: 'from-purple-500 to-violet-600',
  },
  {
    id: 'approved-only',
    title: 'Approved Apps Only',
    description: 'Maximum control over every app. Only apps you specifically approve can be installed.',
    store: 'Shomri App Store',
    icon: ListChecks,
    gradient: 'from-green-500 to-emerald-600',
  },
  {
    id: 'open-blocklist',
    title: 'Open App Store + Block List',
    description: 'Full access to the Apple App Store with the ability to block specific apps.',
    store: 'Apple App Store',
    icon: Store,
    gradient: 'from-sky-500 to-blue-600',
  },
];

const settingsItems = [
  {
    id: 'browser',
    title: 'Browser Policies',
    subtitle: 'Manage website access and content filtering',
    icon: Globe,
    gradient: 'from-blue-500 to-cyan-600',
    path: 'browser',
  },
  {
    id: 'general',
    title: 'General Restrictions',
    subtitle: 'Device features and system settings',
    icon: Shield,
    gradient: 'from-orange-500 to-red-600',
    path: 'general',
  },
];

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { serial } = useEffectiveSerial();
  const { currentMode, isLoading } = useDeviceAppMode();

  // Find the current option based on real mode from Convex
  const currentOption = appManagementOptions.find((opt) => opt.id === currentMode) || appManagementOptions[0];
  const CurrentIcon = currentOption.icon;

  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-background to-muted/30 pb-28">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/70 backdrop-blur-2xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 rounded-full hover:bg-muted/50 active:scale-95 transition-all"
            >
              <ChevronLeft className="w-6 h-6 text-foreground" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-foreground">Settings</h1>
              <p className="text-[13px] text-muted-foreground">Manage device restrictions</p>
            </div>
          </div>
          <ProfileAvatar />
        </div>
      </div>

      <div className="px-4 pt-6">
        {/* App Management Section */}
        <div className="mb-6">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-3 px-1">
            App Management
          </p>
          <button
            onClick={() => navigate(`/${serial}/settings/app-management`)}
            className="w-full bg-background/60 backdrop-blur-xl rounded-2xl border border-white/15 shadow-[0_4px_16px_rgba(0,0,0,0.08),inset_0_1px_1px_rgba(255,255,255,0.1)] p-4 hover:shadow-lg active:scale-[0.98] transition-all duration-200 group"
          >
            <div className="flex items-center justify-between">
              {isLoading ? (
                <div className="flex items-center gap-3 flex-1">
                  <Skeleton className="w-10 h-10 rounded-xl" />
                  <div>
                    <Skeleton className="h-3 w-20 mb-1" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-xl bg-gradient-to-br ${currentOption.gradient} flex items-center justify-center shadow-lg`}
                  >
                    <CurrentIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-[12px] text-muted-foreground">Currently using</p>
                    <p className="font-semibold text-[15px] text-foreground">{currentOption.title}</p>
                  </div>
                </div>
              )}
              <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
          </button>
        </div>

        {/* Content Controls Section */}
        <div>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-3 px-1">
            Content Controls
          </p>
          <div className="space-y-3">
            {settingsItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => navigate(`/${serial}/settings/${item.path}`)}
                  className="w-full bg-background/60 backdrop-blur-xl rounded-2xl border border-white/15 shadow-[0_4px_16px_rgba(0,0,0,0.08),inset_0_1px_1px_rgba(255,255,255,0.1)] p-4 hover:shadow-lg active:scale-[0.98] transition-all duration-200 group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-lg`}
                      >
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-[15px] text-foreground">{item.title}</p>
                        <p className="text-[13px] text-muted-foreground">{item.subtitle}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
